import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const projects = [
  {
    title: 'Clutch Logistics',
    image: './images/clutch_logistics.png',
    description:
      'An integrated solution combining a web application with the Clutch Driver App. Designed for exporters and large-scale logistics, it streamlines asset management, transport scheduling, and fleet monitoring. The Driver App provides real-time updates, GPS tracking, and easy reporting — enhancing communication between dispatch teams and drivers.',
    tags: ['Software Development', 'Machine Learning', 'AWS Infrastructure'],
    internalLink: '/logistics',
  },
  {
    title: 'Clutch Booking',
    image: './images/booking_img.png',
    description:
      'Clutch Booking simplifies booking management for time-sensitive industries. It allows site managers to create customised booking slots, track availability in real-time, and optimise scheduling. When integrated with Clutch Logistics, it streamlines operations and improves communication across scheduling, transport, and asset management.',
    tags: ['Software Development', 'Web Development', 'AWS Infrastructure'],
    internalLink: '/bookings',
  },
  {
    title: 'Mahonys Transport Services Website',
    image: '/images/mahonys-website.png',
    description:
      'We designed and built the Mahonys Transport Services website — a modern, responsive platform to streamline their logistics operations. With integrated booking systems and compliance management tools, the website enhances fleet tracking and task coordination across all devices.',
    tags: ['Web Development', 'Software Development'],
    website: 'https://mahonystransport.com.au',
  },
  {
    title: 'Grainpac Melbourne Website',
    image: '/images/grainpac-website.png',
    description:
      'We developed the Grainpac Melbourne website to provide an easy-to-use, professional interface for their grain packing and logistics services. Our team integrated accreditation and certification features, making it simple for customers to access vital information across all devices.',
    tags: ['Web Development'],
    website: 'https://www.grainpac.com.au/',
  },
  {
    title: 'Clutch Driver Application',
    image: '/images/Mobile_clutch.png',
    description:
      'A user-friendly mobile tool designed to enhance communication and efficiency for drivers in the logistics and grain transport industries. Drivers can receive real-time job assignments, update delivery statuses, track routes, and submit reports directly from their smartphones.',
    tags: ['Software Development', 'Machine Learning', 'AWS Infrastructure'],
  },
  {
    title: 'Mahonys Transport ERP System',
    image: '/images/transaction-img.png',
    description:
      'A custom ERP system built for Mahonys Transport using PowerApps. This solution enables them to manage orders, process them in the packer schedule application, and track stock levels through the transaction app — automating compliance processes and reducing human error.',
    tags: ['Software Development', 'BPO Services'],
  },
];

const Portfolio = () => {
  return (
    <section className="section-padding bg-white relative overflow-hidden">
      <div className="absolute inset-0 bg-mesh-grid pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border-subtle to-transparent" />

      <div className="section-container relative">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="section-label">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
            </svg>
            Our Portfolio
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">
            Projects That<br />
            <span className="text-gradient-blue">Drive Real Results</span>
          </h2>
          <p className="text-secondary text-lg max-w-2xl mx-auto">
            Explore our recent work and see how we've helped Australian businesses transform their digital operations.
          </p>
        </motion.div>

        {/* Projects grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {projects.map((project, index) => {
            const linkEl = project.website ? (
              <a
                href={project.website}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-accent text-white rounded-full flex items-center justify-center hover:bg-accent-hover hover:shadow-glow-sm transition-all duration-200 flex-shrink-0"
                aria-label={`Visit ${project.title}`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
                </svg>
              </a>
            ) : project.internalLink ? (
              <Link
                to={project.internalLink}
                className="w-10 h-10 bg-accent text-white rounded-full flex items-center justify-center hover:bg-accent-hover hover:shadow-glow-sm transition-all duration-200 flex-shrink-0"
                aria-label={`View ${project.title}`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
                </svg>
              </Link>
            ) : null;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (index % 2) * 0.1, duration: 0.5 }}
                className="rounded-2xl border border-border-subtle bg-white shadow-card hover:border-accent hover:shadow-glow-sm transition-all duration-300 overflow-hidden group"
              >
                {/* Image */}
                <div className="aspect-video overflow-hidden bg-surface-1">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <h3 className="text-primary font-bold text-xl leading-snug">{project.title}</h3>
                    {linkEl}
                  </div>
                  <p className="text-secondary text-sm leading-relaxed mb-4">{project.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag, ti) => (
                      <span
                        key={ti}
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default Portfolio;
